(function () {
  'use strict';

  /**
   * Shopify Inbox mobile positioning controller
   * Prestige-safe, app-safe, isolated, and non-blocking.
   *
   * What it does:
   * - Waits for Shopify Inbox to be injected into the DOM
   * - Positions the CLOSED launcher below the payment icons on mobile
   * - Restores the OPEN chat to full-screen on mobile
   * - Hides the empty instant-answers block inside the open shadow root on mobile
   *
   * Important:
   * - Replace PAYMENT_SELECTOR with the correct selector for your footer payment icons wrapper.
   * - Test on real mobile devices and iOS Safari.
   */

  var CONFIG = {
    chatSelector: '#ShopifyChat',
    chatHostWrapperSelector: '#shopify-chat',

    /**
     * Replace this with your actual Prestige payment icons wrapper selector.
     * Common candidates:
     * .list-payment
     * .Footer__PaymentList
     * .Footer__Aside .list-payment
     */
    paymentSelector: '.payment-methods',

    mobileBreakpoint: 767,
    gap: 8,
    rightOffset: 16,

    /**
     * If true, the launcher will only be positioned when the payment block exists.
     * If false, it falls back to bottom-right fixed spacing.
     */
    requirePaymentBlock: false,

    /**
     * Fallback launcher position when payment block is not found.
     */
    fallbackBottom: 96,
    fallbackRight: 16,

    /**
     * Internal Shopify Inbox shadow-DOM selector to hide on mobile.
     * This may change if Shopify updates the app markup.
     */
    emptyInstantAnswersSelector: '.interstitial-view__instant-answers-list--empty'
  };

  var state = {
    initialized: false,
    rafId: null,
    mutationObserver: null,
    resizeObserver: null,
    lastTop: null,
    lastRight: null,
    lastMode: null
  };

  function isMobileViewport() {
    return window.innerWidth <= CONFIG.mobileBreakpoint;
  }

  function getChatElement() {
    return document.querySelector(CONFIG.chatSelector);
  }

  function getPaymentElement() {
    return document.querySelector(CONFIG.paymentSelector);
  }

  function isChatOpen(chat) {
    return !!chat && chat.getAttribute('is-open') === 'true';
  }

  function setImportantStyle(element, property, value) {
    if (!element) return;
    element.style.setProperty(property, value, 'important');
  }

  function clearStyle(element, property) {
    if (!element) return;
    element.style.removeProperty(property);
  }

  function hideEmptyInstantAnswers(chat) {
    if (!chat || !chat.shadowRoot || !isMobileViewport()) return;

    var emptyBlock = chat.shadowRoot.querySelector(CONFIG.emptyInstantAnswersSelector);
    if (emptyBlock && emptyBlock.style.display !== 'none') {
      emptyBlock.style.display = 'none';
    }
  }

  function applyOpenState(chat) {
    if (!chat) return;

    setImportantStyle(chat, 'position', 'fixed');
    setImportantStyle(chat, 'top', '0px');
    setImportantStyle(chat, 'right', '0px');
    setImportantStyle(chat, 'bottom', '0px');
    setImportantStyle(chat, 'left', '0px');
    setImportantStyle(chat, 'width', '100vw');
    setImportantStyle(chat, 'height', '100dvh');
    setImportantStyle(chat, 'max-height', '100dvh');

    hideEmptyInstantAnswers(chat);

    state.lastMode = 'open';
  }

  function applyClosedStateAbsolute(chat, topPx, rightPx) {
    if (!chat) return;

    var nextTop = String(topPx);
    var nextRight = String(rightPx);

    if (
      state.lastMode === 'closed-absolute' &&
      state.lastTop === nextTop &&
      state.lastRight === nextRight
    ) {
      hideEmptyInstantAnswers(chat);
      return;
    }

    setImportantStyle(chat, 'position', 'absolute');
    setImportantStyle(chat, 'top', nextTop + 'px');
    setImportantStyle(chat, 'right', nextRight + 'px');
    setImportantStyle(chat, 'bottom', 'auto');
    setImportantStyle(chat, 'left', 'auto');
    setImportantStyle(chat, 'width', 'auto');
    setImportantStyle(chat, 'height', 'auto');
    clearStyle(chat, 'max-height');

    hideEmptyInstantAnswers(chat);

    state.lastTop = nextTop;
    state.lastRight = nextRight;
    state.lastMode = 'closed-absolute';
  }

  function applyClosedStateFallback(chat) {
    if (!chat) return;

    if (state.lastMode === 'closed-fallback') {
      hideEmptyInstantAnswers(chat);
      return;
    }

    setImportantStyle(chat, 'position', 'fixed');
    clearStyle(chat, 'top');
    setImportantStyle(chat, 'right', CONFIG.fallbackRight + 'px');
    setImportantStyle(chat, 'bottom', CONFIG.fallbackBottom + 'px');
    setImportantStyle(chat, 'left', 'auto');
    setImportantStyle(chat, 'width', 'auto');
    setImportantStyle(chat, 'height', 'auto');
    clearStyle(chat, 'max-height');

    hideEmptyInstantAnswers(chat);

    state.lastTop = null;
    state.lastRight = String(CONFIG.fallbackRight);
    state.lastMode = 'closed-fallback';
  }

  function updatePosition() {
    state.rafId = null;

    var chat = getChatElement();
    if (!chat) return;

    if (!isMobileViewport()) {
      state.lastMode = 'desktop';
      return;
    }

    if (isChatOpen(chat)) {
      applyOpenState(chat);
      return;
    }

    var paymentElement = getPaymentElement();

    if (!paymentElement) {
      if (CONFIG.requirePaymentBlock) return;
      applyClosedStateFallback(chat);
      return;
    }

    var rect = paymentElement.getBoundingClientRect();
    var top = Math.round(window.scrollY + rect.bottom + CONFIG.gap);
    var right = CONFIG.rightOffset;

    applyClosedStateAbsolute(chat, top, right);
  }

  function requestUpdate() {
    if (state.rafId !== null) return;
    state.rafId = window.requestAnimationFrame(updatePosition);
  }

  function bindWindowEvents() {
    window.addEventListener('load', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    window.addEventListener('scroll', requestUpdate, { passive: true });
  }

  function bindMutationObserver() {
    if (state.mutationObserver) return;

    state.mutationObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        var mutation = mutations[i];

        if (mutation.type === 'childList') {
          requestUpdate();
          return;
        }

        if (
          mutation.type === 'attributes' &&
          mutation.target &&
          mutation.target.matches &&
          mutation.target.matches(CONFIG.chatSelector)
        ) {
          requestUpdate();
          return;
        }
      }
    });

    state.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['is-open', 'style']
    });
  }

  function bindResizeObserver() {
    if (!('ResizeObserver' in window) || state.resizeObserver) return;

    state.resizeObserver = new ResizeObserver(function () {
      requestUpdate();
    });

    var paymentElement = getPaymentElement();
    if (paymentElement) {
      state.resizeObserver.observe(paymentElement);
    }
  }

  function reconnectResizeObserver() {
    if (!('ResizeObserver' in window) || !state.resizeObserver) return;

    state.resizeObserver.disconnect();

    var paymentElement = getPaymentElement();
    if (paymentElement) {
      state.resizeObserver.observe(paymentElement);
    }
  }

  function init() {
    if (state.initialized) return;
    state.initialized = true;

    bindWindowEvents();
    bindMutationObserver();
    bindResizeObserver();
    requestUpdate();

    /**
     * Reconnect ResizeObserver after initial app injection / section rendering changes.
     * This is lightweight and safe.
     */
    setTimeout(function () {
      reconnectResizeObserver();
      requestUpdate();
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();